/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Google.Headings = NO -->*/
/*<!-- vale Google.Headings = YES -->*/
/*<!-- vale Google.Headings = NO -->*/
/*<!-- vale Google.Headings = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1776729600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Apr 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "pendo",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Pendo",
          "depth": 0,
          "id": "connect-to-pendo"
        }, {
          "value": "Create Pendo Integration Key",
          "depth": 1,
          "id": "create-pendo-integration-key"
        }, {
          "value": "Access Track Event Shared Secret",
          "depth": 1,
          "id": "access-track-event-shared-secret"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Objects",
          "depth": 1,
          "id": "objects"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h3: "h3",
      ol: "ol",
      li: "li",
      img: "img",
      ul: "ul",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Ideally, your data warehouse should contain all your customer data. When that's the case, using your warehouse as the source for your Pendo data enables richer, more granular analysis. You can sync enriched data from your warehouse and data from other engagement tools. Moreover, you can ensure that the data in Pendo consumed by your product and growth teams is always up-to-date and in the expected format."
      }), "\n", _jsx(_components.p, {
        children: "If your company also uses a Business Intelligence tool such as Looker or Mode alongside Pendo, you can ensure that the data is consistent across both systems since your data warehouse is the only true source of data."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync records as events to Pendo, often in the form of a track call"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://engageapi.pendo.io",
                children: "Track API reference"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Objects"
              })
            }), _jsx(_components.td, {
              children: "Update visitor, account, and parent account objects"
            }), _jsx(_components.td, {
              children: "Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://engageapi.pendo.io",
                children: "API overview"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-pendo",
        children: "Connect to Pendo"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Pendo"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Pendo with an ", _jsx(_components.strong, {
          children: "Integration Key"
        }), " and ", _jsx(_components.strong, {
          children: "Track Event Shared Secret"
        }), "."]
      }), "\n", "\n", _jsx(_components.h3, {
        id: "create-pendo-integration-key",
        children: "Create Pendo Integration Key"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["In your ", _jsx(_components.a, {
          href: "https://app.pendo.io/admin/",
          children: "Pendo admin"
        }), " go to ", _jsx(_components.strong, {
          children: "Settings"
        }), " > ", _jsx(_components.strong, {
          children: "Integrations"
        }), " > ", _jsx(_components.strong, {
          children: "Integration Keys"
        }), ". Then follow the instructions below:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "+ Add Integration Key"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a meaningful ", _jsx(_components.strong, {
            children: "description"
          }), " for your Integration Key, for example, \"Hightouch Integration.\""]
        }), "\n", _jsxs(_components.li, {
          children: ["Provide the key ", _jsx(_components.strong, {
            children: "Allow writes"
          }), " access."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-pendo-int-key.png",
          alt: "Integration Key creation in the Pendo UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create"
          }), ". Securely store this key for use in Hightouch."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about Integration Keys, refer to Pendo's ", _jsx(_components.a, {
          href: "https://support.pendo.io/hc/en-us/articles/9491198203547-Pendo-Integration-Key#requirements-0-0",
          children: "Integration Key article"
        }), "."]
      }), "\n", "\n", _jsx(_components.h3, {
        id: "access-track-event-shared-secret",
        children: "Access Track Event Shared Secret"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["To access your Track Event Shared Secret go to ", _jsx(_components.strong, {
          children: "Subscription Settings"
        }), " > ", _jsx(_components.strong, {
          children: "Choose your App"
        }), " > ", _jsx(_components.strong, {
          children: "App Details"
        }), " in your ", _jsx(_components.a, {
          href: "https://app.pendo.io/admin/",
          children: "Pendo admin"
        }), ". You must be a Pendo Admin to have access to this Secret Key. You can find more information in Pendo's ", _jsx(_components.a, {
          href: "https://support.pendo.io/hc/en-us/articles/360032294291-Track-Events-Configuration#segment-com-0-5",
          children: "events tracking documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Pendo destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Pendo destination you want to sync to."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsx(_components.p, {
        children: "The Pendo integration lets you send events to Pendo with custom properties. You must input the following information:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event name"
          }), ": either a model column containing the event name or a static value."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Timestamp"
          }), ": a model column containing the time the event occurred; if you leave this empty, Pendo uses the time the event arrives at the server."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Pendo suggests that you always supply both a Visitor ID (required) and Account ID (strongly recommended)."
      }), "\n", _jsx(_components.p, {
        children: "In addition, you can choose to map additional fields to your Pendo event payload in the custom properties."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-pendo-event-field-mapping.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "objects",
        children: "Objects"
      }), "\n", _jsx(_components.p, {
        children: "The Pendo integration allows users to update existing visitors, accounts, and parent accounts with metadata—both agent and custom fields."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You have to choose how to match records from your model to objects in Pendo. Select a column from your model that corresponds to your Pendo profile ID. The following example screenshot shows mapping the model's ", _jsx(_components.strong, {
          children: "id"
        }), " column to Pendo's ", _jsx(_components.strong, {
          children: "Visitor ID"
        }), " field."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-pendo-match.png",
          alt: "Record matching in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "If users aren't found in Pendo, Hightouch automatically skips the row."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Pendo has two field types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Agent fields"
          }), " are API-created fields the agent integrated on your website or app."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom fields"
          }), " are editable in the UI and through the API,"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can choose which fields you want to update on your profiles in Pendo. The following example screenshot shows mapping the model's ", _jsx(_components.strong, {
          children: "firstname"
        }), " column to the ", _jsx(_components.strong, {
          children: "firstname"
        }), " field in Pendo."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-pendo-field-mapping.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "You cannot create agent fields in the Pendo UI. If you want to write to a new field in Pendo that doesn't exist, you can pass in the desired field's name in Hightouch. The option to create that new field will appear, and if you select it, the field will be created during the next sync."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-pendo-create-new-field.png",
          alt: "Creating a new Pendo field in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can create custom fields in Pendo by going to ", _jsxs(_components.a, {
          href: "https://app.pendo.io/admin/mappings",
          children: [_jsx(_components.strong, {
            children: "Settings"
          }), " > ", _jsx(_components.strong, {
            children: "Data Mappings"
          })]
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "split-retries",
        children: "Split retries"
      }), "\n", _jsxs(_components.p, {
        children: ["Pendo counts all events in a batch as rejected if the request contains a single invalid event.\nTo pinpoint which records are getting rejected with which errors and reduce the number of valid records that get retried,\nyou can enable ", _jsx(_components.a, {
          href: "/syncs/retries#split-retries",
          children: _jsx(_components.strong, {
            children: "split retries"
          })
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the document in Pendo"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Remove the document from Pendo"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
